import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Branch {
  id: number;
  name: string;
  code?: string;
  type?: string;
}

interface BranchContextType {
  activeBranch: Branch | null;
  setActiveBranch: (branch: Branch | null) => void;
  branches: Branch[];
  setBranches: (branches: Branch[]) => void;
  userBranchId: number | null;
}

const BranchContext = createContext<BranchContextType>({
  activeBranch: null,
  setActiveBranch: () => {},
  branches: [],
  setBranches: () => {},
  userBranchId: null,
});

export const useBranch = () => useContext(BranchContext);

export const BranchProvider = ({ children }: { children: ReactNode }) => {
  const [activeBranch, setActiveBranch] = useState<Branch | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [userBranchId, setUserBranchId] = useState<number | null>(null);

  // On mount, load from localStorage and determine user's branch
  useEffect(() => {
    // Get user's branch from userdata
    const storedUser = localStorage.getItem('userdata');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const branchId = userData.ms_branch_id || userData.id_branch || null;
        if (branchId) setUserBranchId(Number(branchId));
      } catch { /* ignore */ }
    }

    // Get stored branch selection
    const storedBranch = localStorage.getItem('activeBranch');
    if (storedBranch) {
      try {
        setActiveBranch(JSON.parse(storedBranch));
      } catch { /* ignore */ }
    }
  }, []);

  // Persist active branch to localStorage
  const handleSetActiveBranch = (branch: Branch | null) => {
    setActiveBranch(branch);
    if (branch) {
      localStorage.setItem('activeBranch', JSON.stringify(branch));
    } else {
      localStorage.removeItem('activeBranch');
    }
  };

  return (
    <BranchContext.Provider
      value={{
        activeBranch,
        setActiveBranch: handleSetActiveBranch,
        branches,
        setBranches,
        userBranchId,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};

export default BranchContext;
